import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedMediaWidget, SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { SmartLinkStatsMediaItem } from 'fym-shared/src/smartlinks/stats';

/** One box of the Media panel (design c1): a block of the page, or a block that was removed. */
export interface MediaStatsRow {
  blockUUID: string;
  /** The block on the page; null for a block that was removed since. */
  widget: SerializedMediaWidget | null;
  /** The place of the block on the page, from 1; null for a removed block. */
  position: number | null;
  /** The numbers; an empty item when the window has none for the block. */
  item: SmartLinkStatsMediaItem;
}

export function emptyMediaItem(blockUUID: string): SmartLinkStatsMediaItem {
  return {
    blockUUID,
    seen: 0,
    plays: 0,
    autoplayPlays: 0,
    unmutes: 0,
    quartiles: [0, 0, 0, 0],
    avgWatchedPct: null,
    clicks: 0,
    visitors: 0,
    series: [],
  };
}

/**
 * The boxes of the Media panel: one per Media block in page order, then the removed blocks that
 * still have numbers in the window, so old numbers still add up (design c, note 3). Pure data.
 */
export function mediaStatsRows(
  items: SmartLinkStatsMediaItem[] | undefined,
  widgets: SerializedWidget[] | null | undefined
): MediaStatsRow[] {
  const byBlock = new Map((items ?? []).map((item) => [item.blockUUID, item] as const));
  const ordered = [...(widgets ?? [])].sort((a, b) => a.position - b.position);
  const onPage: MediaStatsRow[] = [];
  ordered.forEach((widget, index) => {
    if (widget.type !== SmartLinkWidgetType.media) {
      return;
    }
    onPage.push({
      blockUUID: widget.uuid,
      widget,
      position: index + 1,
      item: byBlock.get(widget.uuid) ?? emptyMediaItem(widget.uuid),
    });
  });
  const pageBlocks = new Set(onPage.map((row) => row.blockUUID));
  const removed = (items ?? [])
    .filter((item) => !pageBlocks.has(item.blockUUID))
    .map((item) => ({ blockUUID: item.blockUUID, widget: null, position: null, item }));
  return [...onPage, ...removed];
}

/** True when the page has a Media block that shows a video now. */
export function hasVideoBlock(widgets: SerializedWidget[] | null | undefined): boolean {
  return (widgets ?? []).some(
    (widget) => widget.type === SmartLinkWidgetType.media && widget.data.media?.kind === 'video'
  );
}

/**
 * The average watched share over every block, weighted by the plays of each block, 0-100. Null
 * with no play.
 */
export function overallWatchedPct(items: SmartLinkStatsMediaItem[] | undefined): number | null {
  let plays = 0;
  let weighted = 0;
  for (const item of items ?? []) {
    if (item.avgWatchedPct !== null && item.plays > 0) {
      plays += item.plays;
      weighted += item.avgWatchedPct * item.plays;
    }
  }
  return plays === 0 ? null : Math.round((weighted / plays) * 10) / 10;
}

/** `part` as a share of `whole`, 0-1; null when the whole is 0. */
export function shareOf(part: number, whole: number): number | null {
  return whole <= 0 ? null : part / whole;
}
